import { Modal } from "antd";
import { useState } from "react";
import ReactCardFlip from "react-card-flip";
import { useTranslation } from "react-i18next";
import { FaFacebookF, FaInstagram, FaTwitter } from "react-icons/fa";
import { IoCallOutline } from "react-icons/io5";
import { MdOutlineAlternateEmail } from "react-icons/md";
import { TbWorld } from "react-icons/tb";
import { useSelector } from "react-redux";
import { createArray } from "../utils/functions";
import { assets } from "./Assets";

const FlipCard = ({ className = "", item, business, flipped = false }) => {
  const { t } = useTranslation();
  const businessSlug = useSelector((state) => state.account.businessSlug);
  const [isFlipped, setIsFlipped] = useState(flipped);

  const rewardUrls = {};
  const businessContact = business?.config?.contact;
  const schemeSlug = item.slug;
  const version = item.rules.schema_version;
  const templates = item.rules.templates;

  const itemLayout = item.rules.layouts[0];
  const cardLayout = {
    gridSize: itemLayout.name.split("_")[1],
    columns: parseInt(itemLayout.name.split("_")[1].split("x")[0], 10),
    rows: parseInt(itemLayout.name.split("_")[1].split("x")[1], 10),
  };

  const businessAssets = assets(businessSlug, version);
  const logoUrl = businessAssets.stampCard(schemeSlug).logo();
  const stampCardBackgroundUrl = businessAssets
    .stampCard(schemeSlug)
    .background.scheme();

  templates[0].rewards?.map((reward) => {
    const stampCardTemplateUrl = businessAssets
      .stampCard(schemeSlug)
      .background.template(reward.name);

    const voucherThumbnailUrl = businessAssets
      .vouchers(schemeSlug)
      .thumbnail(reward.reward_type);

    const inkingUrl = businessAssets
      .stampCard(schemeSlug)
      .inking(reward.reward_type);

    const targetUrl = businessAssets
      .stampCard(schemeSlug)
      .target(reward.reward_type);

    rewardUrls[reward.reward_type] = {
      stampCardTemplateUrl,
      voucherThumbnailUrl,
      inkingUrl,
      targetUrl,
      positionOnCard: reward.position_on_card,
      title: reward.title,
      terms: reward.terms,
    };
  });

  const defaultPositions = createArray(itemLayout.number_of_positions).filter(
    (num) =>
      !new Set(
        Object.values(rewardUrls).map((item) => item.positionOnCard),
      ).has(num),
  );

  const defaultReward = templates[0].rewards?.[0];
  const defaultRewardUrls = {
    stampCardTemplateUrl: businessAssets
      .stampCard(schemeSlug)
      .background.template(defaultReward.name),
    inkingUrl: businessAssets.stampCard(schemeSlug).inking("default"),
    targetUrl: businessAssets.stampCard(schemeSlug).target("default"),
    positionOnCard: defaultPositions,
    title: defaultReward.title,
    terms: defaultReward.terms,
  };
  rewardUrls.default = defaultRewardUrls;

  const rewards = createArray(itemLayout.number_of_positions).map(
    (_, index) => {
      const imageElements = Object.values(rewardUrls).flatMap((reward) => {
        const positions = Array.isArray(reward.positionOnCard)
          ? reward.positionOnCard
          : [reward.positionOnCard];
        if (positions.includes(index + 1)) {
          return (
            <img
              key={reward.title + index}
              src={reward.targetUrl}
              alt={reward.title}
              className="reward-img"
            />
          );
        }
        return [];
      });
      return imageElements;
    },
  );

  const showEncourageMsg = item.rules.show_encouragement_messaging;
  const encourageMsg = item.rules.templates[0].messaging[0].encouragement;

  const handleCardClick = () => {
    setIsFlipped(!isFlipped);
  };

  const info = (termsText) => {
    Modal.info({
      title: t("flipcard_terms_title"), // translated
      content: (
        <div>
          <p>{termsText}</p>
        </div>
      ),
      onOk() {},
      okButtonProps: {
        style: {
          backgroundColor: "#F11E69",
          borderRadius: "5px",
        },
      },
    });
  };

  const cardFrontColor = item.rules.colors.card;
  return (
    <div className="card-container" onClick={handleCardClick}>
      <ReactCardFlip
        className={className}
        isFlipped={isFlipped}
        flipDirection="horizontal"
      >
        <div
          className="flip-card-front"
          style={{
            backgroundColor: cardFrontColor,
            backgroundImage: `url(${stampCardBackgroundUrl})`,
          }}
        >
          <div className="flip-card-front-header">
            {<img className="logo" src={logoUrl} alt="" /> || null}
            {showEncourageMsg && (
              <h6
                style={{ textAlign: "center", color: item.rules.colors.text }}
              >
                {encourageMsg}
              </h6>
            )}
          </div>
          <div
            className="rewards"
            style={{
              display: "grid",
              rowGap: "5px",
              gridTemplateColumns: `repeat(${cardLayout.columns}, 1fr)`,
              gridTemplateRows: `repeat(${cardLayout.rows}, 1fr)`,
            }}
          >
            {rewards}
          </div>
        </div>

        <div
          style={{
            backgroundColor: item.rules.colors.cardBack,
            color: item.rules.colors.textBack,
          }}
          className="flip-card-back"
        >
          <div>
            <h5>{business?.name}</h5>
            {business?.config?.locations_hint && (
              <h6>{business?.config?.locations_hint}</h6>
            )}
          </div>

          {item?.rules?.extra_info && (
            <h6 className="extra_info">{item?.rules?.extra_info}</h6>
          )}
          <ul>
            {businessContact?.facebook_tag && (
              <li>
                <a
                  href={`https://facebook.com/${businessContact?.facebook_tag}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaFacebookF
                    className="social-icon"
                    style={{
                      color: item?.rules.colors.cardBack,
                      backgroundColor: item.rules.colors.textBack,
                    }}
                  />
                  <h6>{businessContact?.facebook_tag}</h6>
                </a>
              </li>
            )}
            {businessContact?.twitter_tag && (
              <li>
                <a
                  href={`https://twitter.com/${businessContact?.twitter_tag}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaTwitter
                    className="social-icon"
                    style={{
                      color: item?.rules.colors.cardBack,
                      backgroundColor: item.rules.colors.textBack,
                    }}
                  />
                  <h6>{businessContact?.twitter_tag}</h6>
                </a>
              </li>
            )}
            {businessContact?.instagram_tag && (
              <li>
                <a
                  href={`https://instagram.com/${businessContact?.instagram_tag}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaInstagram
                    className="social-icon"
                    style={{
                      color: item?.rules.colors.cardBack,
                      backgroundColor: item.rules.colors.textBack,
                    }}
                  />
                  <h6>{businessContact?.instagram_tag}</h6>
                </a>
              </li>
            )}
            {businessContact?.email && (
              <li>
                <a href={`mailto:${businessContact?.email}`}>
                  <MdOutlineAlternateEmail
                    className="social-icon"
                    style={{
                      color: item?.rules.colors.cardBack,
                      backgroundColor: item.rules.colors.textBack,
                    }}
                  />
                  <h6>{businessContact?.email}</h6>
                </a>
              </li>
            )}
            {businessContact?.phone && (
              <li>
                <a href={`tel:${businessContact?.phone}`}>
                  <IoCallOutline
                    className="social-icon"
                    style={{
                      color: item?.rules.colors.cardBack,
                      backgroundColor: item.rules.colors.textBack,
                    }}
                  />
                  <h6>{businessContact?.phone}</h6>
                </a>
              </li>
            )}
            {businessContact?.website && (
              <li>
                <a
                  href={businessContact?.website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <TbWorld
                    className="social-icon"
                    style={{
                      color: item?.rules.colors.cardBack,
                      backgroundColor: item.rules.colors.textBack,
                    }}
                  />
                  <h6>{businessContact?.website}</h6>
                </a>
              </li>
            )}
          </ul>
          {item.rules.terms_text && (
            <a href="#terms" onClick={() => info(item.rules.terms_text)}>
              {t("flipcard_terms")}
            </a>
          )}
        </div>
      </ReactCardFlip>
    </div>
  );
};

export default FlipCard;
